import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ConsequenceDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { ConsequenceRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  Consequence_Bool_Exp,
  GetConsequencesQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetConsequencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import { CONSEQUENCE_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  normaliseArrayFilter,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useConsequenceGraphqlVariables = (args: InternalArgs) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Consequence_Bool_Exp>(
    Parent_Type_Enum.Consequence
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: CONSEQUENCE_DATE_PREDICATES,
    tagsWhere: (ids) => ({ issue: { tags: { TagTypeId: { _in: ids } } } }),
    departmentsWhere: (ids) => ({
      issue: { departments: { DepartmentTypeId: { _in: ids } } },
    }),
    ownershipWhere: (conditions) => ({
      issue: conditions.issueFilterConditions,
    }),
  });

  return { where };
};

type InternalArgs = UseGetConsequenceRegisterArgs & {
  entityIds?: string[];
};

export type UseGetConsequenceRegisterArgs =
  DashboardFilterArgs<ConsequenceDashboardDateField>;

const useGetConsequenceRegisterBase = createQueryHook<
  InternalArgs,
  ConsequenceRegisterResponseRow[],
  GetConsequencesQuery
>({
  trpcQueryOptions: (
    trpc,
    { entityIds, tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.consequence.register.queryOptions({
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: (data) => ({ consequence: data }),
  graphqlDocument: GetConsequencesDocument,
  graphqlVariables: useConsequenceGraphqlVariables,
});

/**
 * Fetches consequences with automatic entity filtering for both GraphQL and
 * tRPC paths. Entity filtering uses useEntityFilter context internally —
 * consumers don't need to pass entityIds. Dashboard filters (COI-691) are all
 * optional.
 */
export const useGetConsequenceRegister = (params: {
  queryArgs: UseGetConsequenceRegisterArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetConsequenceRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
